import { useState, useEffect } from 'react';
import { Product, CategoryType, products as mockProducts } from '../../mocks/products';
import { useCategoryContext, CategoryItem } from '../../contexts/CategoryContext';
import CategorySortPanel from './CategorySortPanel';
import CategoryManagePanel from './CategoryManagePanel';

interface ProductEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product | null;
  onSave: (product: Product) => void;
  onDelete?: (id: string) => void;
}

// mechanism 문자열을 배열로 파싱
function parseMechanism(mechanism?: string): string[] {
  if (!mechanism) return [''];
  const lines = mechanism.split('\\n').map((l) => l.trim()).filter(Boolean);
  return lines.length > 0 ? lines : [''];
}

function getInitialFormData(p?: Product | null): Partial<Product> {
  return {
    id: '',
    name: '',
    nameEn: '',
    subtitle: '',
    volume: '',
    category: 'disease',
    description: '',
    thumbnail: '',
    image: '',
    features: [''],
    usage: '',
    dosage: '',
    recommendation: '',
    mechanism: '',
    beforeAfterImage: '',
    usageDetail: [''],
    caution: [''],
    ...p,
  };
}

export default function ProductEditModal({ isOpen, onClose, product, onSave, onDelete }: ProductEditModalProps) {
  const { categoryList, addCategory, reorderCategories, deleteCategory } = useCategoryContext();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState<Partial<Product>>(getInitialFormData(product));

  // 성분 분석 항목 배열로 별도 관리
  const [mechanismItems, setMechanismItems] = useState<string[]>(
    parseMechanism(product?.mechanism)
  );

  // product prop이 바뀔 때마다 폼 데이터 동기화 (수정 모드 진입 시 최신 데이터 반영)
  useEffect(() => {
    setFormData(getInitialFormData(product));
    setMechanismItems(parseMechanism(product?.mechanism));
  }, [product?.id]);

  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showManageCategory, setShowManageCategory] = useState(false);
  const [showCategoryManage, setShowCategoryManage] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDesc, setNewCategoryDesc] = useState('');

  const [relatedProducts, setRelatedProducts] = useState<string[]>([]);
  const [showProductPicker, setShowProductPicker] = useState(false);

  const selectedCategory = categoryList.find((c) => c.id === formData.category);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // mechanism 배열을 \n 구분 문자열로 합쳐서 저장
    const mechanismStr = mechanismItems.filter((m) => m.trim()).join('\\n');
    onSave({ ...formData, mechanism: mechanismStr } as Product);
  };

  const handleCategoryChange = (categoryId: string) => {
    setFormData({ ...formData, category: categoryId as CategoryType });
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    const newId = addCategory(newCategoryName.trim(), newCategoryDesc.trim());
    setFormData({ ...formData, category: newId as CategoryType });
    setNewCategoryName('');
    setNewCategoryDesc('');
    setShowAddCategory(false);
  };

  // 성분 분석 항목 핸들러
  const handleMechanismChange = (index: number, value: string) => {
    const next = [...mechanismItems];
    next[index] = value;
    setMechanismItems(next);
  };

  const addMechanismItem = () => {
    setMechanismItems([...mechanismItems, '']);
  };

  const removeMechanismItem = (index: number) => {
    setMechanismItems(mechanismItems.filter((_, i) => i !== index));
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...(formData.features || [])];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeature = () => {
    setFormData({ ...formData, features: [...(formData.features || []), ''] });
  };

  const removeFeature = (index: number) => {
    const newFeatures = (formData.features || []).filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const handleUsageDetailChange = (index: number, value: string) => {
    const newUsage = [...(formData.usageDetail || [])];
    newUsage[index] = value;
    setFormData({ ...formData, usageDetail: newUsage });
  };

  const addUsageDetail = () => {
    setFormData({ ...formData, usageDetail: [...(formData.usageDetail || []), ''] });
  };

  const removeUsageDetail = (index: number) => {
    const newUsage = (formData.usageDetail || []).filter((_, i) => i !== index);
    setFormData({ ...formData, usageDetail: newUsage });
  };

  const handleCautionChange = (index: number, value: string) => {
    const newCaution = [...(formData.caution || [])];
    newCaution[index] = value;
    setFormData({ ...formData, caution: newCaution });
  };

  const addCaution = () => {
    setFormData({ ...formData, caution: [...(formData.caution || []), ''] });
  };

  const removeCaution = (index: number) => {
    const newCaution = (formData.caution || []).filter((_, i) => i !== index);
    setFormData({ ...formData, caution: newCaution });
  };

  const toggleRelatedProduct = (productId: string) => {
    if (relatedProducts.includes(productId)) {
      setRelatedProducts(relatedProducts.filter((id) => id !== productId));
    } else if (relatedProducts.length < 4) {
      setRelatedProducts([...relatedProducts, productId]);
    }
  };

  const removeRelatedProduct = (productId: string) => {
    setRelatedProducts(relatedProducts.filter((id) => id !== productId));
  };

  const getRelatedProductInfo = (productId: string) => {
    return mockProducts.find((p) => p.id === productId);
  };

  const availableProducts = mockProducts.filter((p) => p.id !== formData.id);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900">
            {product ? '상품 수정' : '상품 등록'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* 1. 제품 상단 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              1. 제품 상단 정보
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">제품명 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                  placeholder="예: 싹황"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">영문 제품명 *</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                  placeholder="예: Sak-hwang"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">용량 *</label>
                <input
                  type="text"
                  value={formData.volume}
                  onChange={(e) => setFormData({ ...formData, volume: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                  placeholder="예: 1L"
                  required
                />
              </div>

              {/* 카테고리 선택 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">카테고리 *</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setShowCategoryManage(!showCategoryManage); setShowManageCategory(false); setShowAddCategory(false); }}
                      className="text-xs text-gray-500 hover:text-gray-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap"
                    >
                      <i className="ri-settings-3-line"></i>
                      카테고리 관리
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => { setShowManageCategory(!showManageCategory); setShowAddCategory(false); setShowCategoryManage(false); }}
                      className="text-xs text-gray-500 hover:text-gray-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap"
                    >
                      <i className="ri-list-ordered"></i>
                      순서 변경
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => { setShowAddCategory(!showAddCategory); setShowManageCategory(false); setShowCategoryManage(false); }}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>
                      카테고리 추가
                    </button>
                  </div>
                </div>

                <select
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                  required
                >
                  {categoryList.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </select>

                {selectedCategory && selectedCategory.description && (
                  <p className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2 leading-relaxed">
                    {selectedCategory.description}
                  </p>
                )}

                {/* 카테고리 관리 패널 */}
                {showCategoryManage && (
                  <CategoryManagePanel
                    categoryList={categoryList}
                    onReorder={reorderCategories}
                    onDelete={deleteCategory}
                    onClose={() => setShowCategoryManage(false)}
                  />
                )}

                {/* 카테고리 순서 변경 패널 - 드래그 앤 드롭 */}
                {showManageCategory && (
                  <CategorySortPanel
                    categoryList={categoryList}
                    onReorder={reorderCategories}
                    onClose={() => setShowManageCategory(false)}
                  />
                )}

                {/* 카테고리 추가 폼 */}
                {showAddCategory && (
                  <div className="mt-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-3">
                    <p className="text-sm font-medium text-emerald-800">새 카테고리 추가</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">카테고리 이름 *</label>
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                        placeholder="예: 토양 개량제"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">부연 설명</label>
                      <textarea
                        value={newCategoryDesc}
                        onChange={(e) => setNewCategoryDesc(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm h-16 resize-none"
                        placeholder="카테고리에 대한 간단한 설명을 입력하세요"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        className="flex-1 bg-emerald-600 text-white py-2 rounded-md text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        추가
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowAddCategory(false); setNewCategoryName(''); setNewCategoryDesc(''); }}
                        className="flex-1 bg-white text-gray-600 py-2 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">요약 상품 설명 *</label>
                <textarea
                  value={formData.recommendation}
                  onChange={(e) => setFormData({ ...formData, recommendation: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm h-20 resize-none"
                  placeholder="간단한 제품 설명을 입력하세요"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">제품 특징 요약 문구</label>
                <input
                  type="text"
                  value={formData.subtitle || ''}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                  placeholder="예: 발효유황"
                />
              </div>
            </div>
          </div>

          {/* 2. 제품 상세 정보 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              2. 제품 상세 정보
            </h3>

            {/* 제품 특징 */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">제품 특징</label>
                <button type="button" onClick={addFeature} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-add-line"></i>추가
                </button>
              </div>
              {(formData.features || []).map((feature, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                    placeholder={`특징 ${index + 1}`}
                  />
                  <button type="button" onClick={() => removeFeature(index)} className="px-3 py-2 text-red-500 hover:text-red-600 cursor-pointer">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>

            {/* 성분 분석 및 메커니즘 - 항목별 추가 UI */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">성분 분석 및 메커니즘</label>
                <button
                  type="button"
                  onClick={addMechanismItem}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>추가
                </button>
              </div>
              <div className="space-y-2">
                {mechanismItems.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <div className="flex-shrink-0 w-6 h-9 flex items-center justify-center text-gray-400 text-xs font-medium">
                      {index + 1}
                    </div>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => handleMechanismChange(index, e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                      placeholder={`성분/메커니즘 항목 ${index + 1} (예: 주요 성분: 발효유황 30%)`}
                    />
                    <button
                      type="button"
                      onClick={() => removeMechanismItem(index)}
                      disabled={mechanismItems.length === 1}
                      className={`px-3 py-2 transition-colors cursor-pointer ${mechanismItems.length === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-red-500 hover:text-red-600'}`}
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-400">각 항목은 제품 상세페이지에서 줄별로 표시됩니다.</p>
            </div>

            {/* 사용 방법 */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">사용 방법</label>
                <button type="button" onClick={addUsageDetail} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-add-line"></i>추가
                </button>
              </div>
              {(formData.usageDetail || []).map((usage, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={usage}
                    onChange={(e) => handleUsageDetailChange(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                    placeholder={`사용 방법 ${index + 1}`}
                  />
                  <button type="button" onClick={() => removeUsageDetail(index)} className="px-3 py-2 text-red-500 hover:text-red-600 cursor-pointer">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>

            {/* 사용 시 주의사항 */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">사용 시 주의사항</label>
                <button type="button" onClick={addCaution} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-add-line"></i>추가
                </button>
              </div>
              {(formData.caution || []).map((caution, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={caution}
                    onChange={(e) => handleCautionChange(index, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                    placeholder={`주의사항 ${index + 1}`}
                  />
                  <button type="button" onClick={() => removeCaution(index)} className="px-3 py-2 text-red-500 hover:text-red-600 cursor-pointer">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>

            {/* 함께 구매하면 좋은 제품 */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  함께 구매하면 좋은 제품
                  <span className="ml-2 text-xs text-gray-400 font-normal">최대 4개</span>
                </label>
                <button type="button" onClick={() => setShowProductPicker(true)} className="text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-search-line"></i>제품 선택
                </button>
              </div>
              {relatedProducts.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {relatedProducts.map((productId) => {
                    const p = getRelatedProductInfo(productId);
                    if (!p) return null;
                    return (
                      <div key={productId} className="relative p-3 bg-gray-50 rounded-lg border border-gray-200 text-center">
                        <button type="button" onClick={() => removeRelatedProduct(productId)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs cursor-pointer">
                          <i className="ri-close-line"></i>
                        </button>
                        <div className="w-12 h-12 mx-auto mb-2 rounded-lg overflow-hidden">
                          <img src={p.thumbnail} alt={p.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <p className="text-xs text-gray-700 font-medium leading-tight">{p.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{p.volume}</p>
                      </div>
                    );
                  })}
                  {relatedProducts.length < 4 && (
                    <button type="button" onClick={() => setShowProductPicker(true)} className="p-3 bg-white rounded-lg border-2 border-dashed border-gray-300 text-center hover:border-emerald-400 transition-colors cursor-pointer">
                      <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center text-gray-400">
                        <i className="ri-add-line text-2xl"></i>
                      </div>
                      <span className="text-xs text-gray-400">추가</span>
                    </button>
                  )}
                </div>
              ) : (
                <div onClick={() => setShowProductPicker(true)} className="w-full h-20 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center gap-2 text-gray-400 text-sm cursor-pointer hover:border-emerald-400 hover:text-emerald-500 transition-colors">
                  <i className="ri-add-circle-line text-lg"></i>
                  제품 선택 버튼을 눌러 추가하세요
                </div>
              )}
            </div>
          </div>

          {/* 3. 이미지 설정 */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              3. 이미지 설정
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">썸네일 이미지 URL</label>
              <input
                type="text"
                value={formData.thumbnail}
                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 text-sm"
                placeholder="썸네일 이미지 URL을 입력하세요"
              />
              {formData.thumbnail && (
                <div className="mt-2 w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                  <img src={formData.thumbnail} alt="썸네일 미리보기" className="w-full h-full object-cover object-top" />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            {/* 삭제 버튼 - 수정 모드일 때만 표시 */}
            {product ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-delete-bin-line"></i>
                상품 삭제
              </button>
            ) : (
              <div />
            )}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-6 py-2.5 text-gray-600 hover:text-gray-800 font-medium cursor-pointer whitespace-nowrap">취소</button>
              <button type="submit" className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                {product ? '수정 저장' : '상품 등록'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* 상품 삭제 확인 팝업 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-2-line text-red-500 text-2xl"></i>
              </div>
              <div>
                <h4 className="text-base font-bold text-gray-900">상품 삭제</h4>
                <p className="text-sm text-gray-500 mt-0.5">{product?.name}</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-6">
              <p className="text-sm text-red-700 font-medium">
                상품을 삭제하면 복구할 수 없습니다.
              </p>
              <p className="text-sm text-red-600 mt-1">
                정말 삭제하시겠습니까?
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  if (product?.id && onDelete) {
                    onDelete(product.id);
                  }
                  setShowDeleteConfirm(false);
                  onClose();
                }}
                className="flex-1 py-2.5 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 제품 선택 팝업 */}
      {showProductPicker && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" onClick={() => setShowProductPicker(false)}>
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-gray-900">함께 구매하면 좋은 제품 선택</h3>
                <p className="text-xs text-gray-500 mt-0.5">최대 4개까지 선택 가능 · 현재 {relatedProducts.length}개 선택됨</p>
              </div>
              <button type="button" onClick={() => setShowProductPicker(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availableProducts.map((p) => {
                  const isSelected = relatedProducts.includes(p.id);
                  const isDisabled = !isSelected && relatedProducts.length >= 4;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => !isDisabled && toggleRelatedProduct(p.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer ${isSelected ? 'border-emerald-500 bg-emerald-50' : isDisabled ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed' : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'}`}
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                        <img src={p.thumbnail} alt={p.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.nameEn} · {p.volume}</p>
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{p.recommendation}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'}`}>
                        {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button type="button" onClick={() => setShowProductPicker(false)} className="bg-emerald-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                선택 완료 ({relatedProducts.length}개)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
